import { useState } from 'react';
import { storage } from '@doxyme/ui/utils';

/**
 * usePreviouslySelected is a custom hook that returns previously selected values
 * from local storage and a function to update them.
 * Stores up to 3 previously selected values.
 * If the value is already in the previously selected values, it is moved to the beginning of the list.
 * updatePreviouslySelected accepts a value and updates the previously selected values accordingly.
 * @param localStorageKey - The key to use for storing the previously selected values.
 * @returns An array containing the previously selected values and a function to update them.
 */
export const usePreviouslySelected = (
  localStorageKey: string | undefined,
): [string[], (value: string) => void] => {
  const [previouslySelected, setPreviouslySelected] = useState<string[]>(
    localStorageKey !== undefined
      ? storage.getItem<string[]>(localStorageKey, true) ?? []
      : [],
  );

  const updatePreviouslySelected = (value: string): void => {
    if (localStorageKey !== undefined) {
      setPreviouslySelected((previous) => {
        const updatedPreviouslySelected = (
          previous.includes(value)
            ? [value, ...previous.filter((selected) => selected !== value)]
            : [value, ...previous]
        ).slice(0, 3);

        storage.setItem(localStorageKey, updatedPreviouslySelected, true);

        return updatedPreviouslySelected;
      });
    }
  };

  return [previouslySelected, updatePreviouslySelected];
};
